import React, { useState } from 'react';
import { Factory, Home, Building2 } from 'lucide-react';
import { P, H2, H3, Note, Loi, Tableau, Ecriture, Section, TheoryLayout } from './TheoryUI';

// ─── ONGLET 1 : TITRES DE PLACEMENT ──────────────────────────────────────────
function TabTitres() {
  return (
    <div>
      <H2>1. Notion de résultat hors exploitation</H2>
      <P>Une entreprise peut investir ses liquidités excédentaires dans des activités sans lien direct avec son activité principale : achat de titres cotés en bourse ou acquisition d'immeubles de rendement. Ces activités accessoires doivent être présentées <strong>séparément</strong> dans le compte de résultat, au 3e niveau.</P>
      <Loi art="Art. 959b al. 2 ch. 8 CO">
        Le compte de résultat présenté par nature contient séparément les charges et produits résultant de participations, des autres postes de l'actif immobilisé financier et des actifs circulants, ainsi que les charges d'intérêts.
      </Loi>
      <Note color="blue">
        Cette séparation est obligatoire dès qu'une entreprise détient des titres ou des immeubles de rendement. Elle permet aux utilisateurs des comptes d'identifier clairement la performance de l'activité principale et celle des investissements accessoires.
      </Note>

      <H2>2. Comptes utilisés pour les titres</H2>
      <Tableau
        headers={['Compte', 'Nature', 'Description']}
        rows={[
          { cells: ['1400 Titres de placement', 'Actif circulant', 'Valeur des titres détenus au bilan (actions, obligations)'] },
          { cells: ['7000 Produits des titres', 'Produit — 3e niveau', 'Dividendes reçus, intérêts, gains de cours réalisés'] },
          { cells: ['7010 Charges des titres', 'Charge — 3e niveau', 'Frais de garde, frais bancaires, pertes de cours'] },
        ]}
      />

      <H2>3. Évaluation des titres cotés en bourse</H2>
      <Loi art="Art. 960b CO — Évaluation des titres cotés">
        Les actifs dont la valeur boursière ou le cours du marché sont établis peuvent être évalués au cours du jour à la date du bilan, même si cette valeur est supérieure à la valeur nominale ou au coût d'acquisition.
      </Loi>
      <P>C'est une exception importante au principe de prudence. En pratique, deux options s'offrent à l'entreprise :</P>
      <Tableau
        headers={['Option', 'Principe', 'Impact', 'Choix fiscal']}
        rows={[
          { cells: ['Évaluation au cours du marché', 'Titres réévalués à la valeur boursière au 31.12', 'Les gains et pertes latents sont comptabilisés', 'Gains latents imposables → souvent défavorable'] },
          { cells: ['Évaluation au coût d\'acquisition', 'Titres maintenus à leur prix d\'achat', 'Seules les plus-values réalisées (lors de la vente) sont enregistrées', 'Imposition différée → fiscalement plus neutre'] },
        ]}
      />
      <Note color="yellow">En pratique fiscale suisse, il est souvent préférable de ne pas réévaluer les titres tant qu'ils ne sont pas vendus — les plus-values latentes ne sont pas encore réalisées et leur imposition peut être différée.</Note>

      <H2>4. Écritures courantes — titres</H2>

      <H3>Achat de 100 actions à CHF 150</H3>
      <Ecriture debit="1400 Titres de placement" credit="1020 Banque" montant="15'000" libelle="Achat 100 actions Nestlé à CHF 150 — cours du jour" />

      <H3>Encaissement d'un dividende CHF 500</H3>
      <Ecriture debit="1020 Banque" credit="7000 Produits des titres" montant="500" libelle="Dividende reçu — Nestlé exercice N" />

      <H3>Vente des 100 actions à CHF 170 — gain CHF 2'000</H3>
      <Ecriture debit="1020 Banque" credit="1400 Titres de placement" montant="15'000" libelle="Sortie des titres au coût d'acquisition" />
      <Ecriture debit="1020 Banque" credit="7000 Produits des titres" montant="2'000" libelle="Plus-value réalisée : (170 − 150) × 100 actions" sub />

      <H3>Perte de cours constatée au 31.12 (valeur marché CHF 13'000 au lieu de CHF 15'000)</H3>
      <Ecriture debit="7010 Charges des titres" credit="1400 Titres de placement" montant="2'000" libelle="Dépréciation titres — cours marché (13'000) inférieur au coût d'acquisition (15'000)" />

      <H3>Réévaluation à la hausse au 31.12 (si option cours du marché choisie)</H3>
      <Ecriture debit="1400 Titres de placement" credit="7000 Produits des titres" montant="xxx" libelle="Réévaluation titres à la valeur boursière au 31.12 — art. 960b CO" />

      <H2>5. Structure du compte de résultat avec hors exploitation</H2>
      <Tableau
        caption="Structure du CR à 4 niveaux — obligatoire dès qu'il y a des titres ou immeubles"
        headers={['Niveau', 'Contenu', 'Comptes']}
        rows={[
          { cells: ['1er niveau', 'Marge brute — résultat commercial', '3000–4999'] },
          { cells: ['2e niveau', 'Résultat d\'exploitation — après charges et amortissements', '5000–6999'] },
          { cells: ['3e niveau', 'Résultat hors exploitation — titres et immeubles', '7000–7999'] },
          { cells: ['4e niveau', 'Résultat exceptionnel et impôts', '8000–8999'], _bold: true },
        ]}
      />

      <H2>6. Points clés</H2>
      {[
        'Les titres et immeubles sont des activités accessoires — séparées du résultat d\'exploitation (art. 959b CO).',
        'Titres cotés : option réévaluation au cours du marché possible (art. 960b CO) — exception au principe de prudence.',
        'Non réévaluation = imposition différée des plus-values → souvent préférable fiscalement.',
        'Dividendes et gains réalisés → compte 7000 Produits des titres.',
        'Pertes de cours et frais de garde → compte 7010 Charges des titres.',
      ].map((pt, i) => (
        <div key={i} style={{ display: 'flex', gap: 10, padding: '5px 0', fontSize: '0.85rem', color: '#475569' }}>
          <span style={{ color: '#2563eb', fontWeight: 700, flexShrink: 0 }}>→</span><span>{pt}</span>
        </div>
      ))}
    </div>
  );
}

// ─── ONGLET 2 : IMMEUBLES DE RENDEMENT ───────────────────────────────────────
function TabImmeubles() {
  return (
    <div>
      <H2>1. Comptes utilisés pour les immeubles</H2>
      <Tableau
        headers={['Compte', 'Nature', 'Description']}
        rows={[
          { cells: ['1600 Immeubles', 'Actif immobilisé', 'Valeur au bilan — max coût d\'acquisition (art. 960a CO)'] },
          { cells: ['1609 FAC Immeubles', 'Actif immobilisé − (correction)', 'Fonds d\'amortissements cumulés — méthode indirecte'] },
          { cells: ['2451 Dette hypothécaire', 'Passif LT', 'Emprunt bancaire hypothécaire'] },
          { cells: ['7500 Produits immeuble', 'Produit — 3e niveau', 'Loyers encaissés de tiers + loyer à soi-même'] },
          { cells: ['7510 Charges immeuble', 'Charge — 3e niveau', 'Entretien, assurances, intérêts hypothécaires, amortissements'] },
          { cells: ['7508 Décompte charges locataires', 'Compte de transit', 'Acomptes chauffage/charges des locataires'] },
        ]}
      />

      <H2>2. Évaluation des immeubles</H2>
      <Loi art="Art. 960a al. 1 CO">
        Lors de sa première comptabilisation, un actif est évalué au plus à son coût d'acquisition ou à son coût de revient. Lors des évaluations subséquentes, la valeur de l'actif ne peut être supérieure à son coût d'acquisition.
      </Loi>
      <Note color="yellow">Contrairement aux titres, la valeur d'un immeuble au bilan ne peut <strong>jamais dépasser son coût d'acquisition</strong> — même si sa valeur de marché a fortement augmenté. C'est là que naissent les réserves latentes immobilières.</Note>
      <P>Exception : les travaux de rénovation ou d'agrandissement qui augmentent durablement la valeur marchande peuvent être <strong>capitalisés</strong> (ajoutés au compte 1600) plutôt que passés en charge. Les travaux d'entretien courants restent en charge (7510).</P>

      <H2>3. Écritures courantes — immeubles</H2>

      <H3>Acquisition d'un immeuble</H3>
      <Ecriture debit="1600 Immeubles" credit="2451 Dette hypothécaire" montant="500'000" libelle="Acquisition immeuble — financement hypothécaire" />
      <Ecriture debit="1600 Immeubles" credit="1020 Banque" montant="100'000" libelle="Apport fonds propres — solde du prix d'achat" sub />

      <H3>Encaissement des loyers mensuels</H3>
      <Ecriture debit="1020 Banque" credit="7500 Produits immeuble" montant="3'500" libelle="Loyers locataires — janvier N" />

      <H3>Paiement des intérêts hypothécaires</H3>
      <Ecriture debit="7510 Charges immeuble" credit="1020 Banque" montant="1'800" libelle="Intérêts hypothécaires — trimestre 1" />

      <H3>Amortissement de l'immeuble (méthode indirecte, 4% dégressif)</H3>
      <Ecriture debit="7510 Charges immeuble" credit="1609 FAC Immeubles" montant="24'000" libelle="Amortissement immeuble 4% × CHF 600'000 (valeur nette)" />

      <H3>Travaux d'entretien courant</H3>
      <Ecriture debit="7510 Charges immeuble" credit="1020 Banque" montant="5'200" libelle="Entretien toiture — charge courante" />

      <H3>Travaux de rénovation capitalisés (augmentation de valeur)</H3>
      <Ecriture debit="1600 Immeubles" credit="2000 Fournisseurs" montant="35'000" libelle="Rénovation salle de bain — capitalisation (augmente la valeur de l'immeuble)" />

      <H2>4. Immeuble mixte — utilisation partielle par l'entreprise</H2>
      <P>Lorsque l'entreprise occupe une partie de l'immeuble pour son propre usage et loue le reste à des tiers, l'intégralité de l'immeuble est considérée comme hors exploitation. Il faut alors comptabiliser un <strong>loyer fictif à soi-même</strong> pour la partie occupée.</P>
      <Note color="blue">
        Ce loyer fictif respecte le <strong>principe de non-compensation</strong> (art. 958c al. 1 ch. 7 CO). Sans lui, on compenserait des produits (loyers tiers) avec des charges (occupation propre), ce qui est interdit.
      </Note>

      <H3>Écriture du loyer à soi-même (mensuel)</H3>
      <Ecriture debit="6000 Charges de locaux (exploitation)" credit="7500 Produits immeuble (hors exploit.)" montant="2'000" libelle="Loyer fictif à soi-même — partie occupée par l'entreprise" />
      <Note color="yellow">
        Effet : le compte 6000 augmente (charge d'exploitation réelle) et le compte 7500 augmente (produit hors exploitation). Les deux niveaux du CR sont correctement alimentés.
      </Note>

      <H2>5. Décompte de chauffage et charges locataires</H2>
      <P>Les charges de chauffage payées par le propriétaire et refacturables aux locataires transitent par le compte <strong>7508 Décompte de charges locataires</strong>.</P>

      <H3>Fonctionnement du compte 7508</H3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, margin: '14px 0' }}>
        <div style={{ border: '1px solid #2563eb', borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ background: '#2563eb', padding: '7px 12px', color: '#fff', fontWeight: 700, fontSize: '0.78rem' }}>Débit — charges à refacturer</div>
          {['Facture de mazout payée', 'Facture d\'entretien chaudière', 'Eau chaude sanitaire'].map((s, i) => (
            <div key={i} style={{ padding: '6px 12px', fontSize: '0.81rem', color: '#475569', borderBottom: '1px solid #f1f5f9' }}>{s}</div>
          ))}
        </div>
        <div style={{ border: '1px solid #059669', borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ background: '#059669', padding: '7px 12px', color: '#fff', fontWeight: 700, fontSize: '0.78rem' }}>Crédit — acomptes reçus des locataires</div>
          {['Acompte mensuel locataire A', 'Acompte mensuel locataire B', 'Acompte mensuel locataire C'].map((s, i) => (
            <div key={i} style={{ padding: '6px 12px', fontSize: '0.81rem', color: '#475569', borderBottom: '1px solid #f1f5f9' }}>{s}</div>
          ))}
        </div>
      </div>

      <Ecriture debit="7508 Décompte charges locataires" credit="1020 Banque" montant="4'800" libelle="Mazout payé — charge à refacturer aux locataires" />
      <Ecriture debit="1020 Banque" credit="7508 Décompte charges locataires" montant="400" libelle="Acompte mensuel locataire A (CHF 400 × 12 = CHF 4'800 attendus)" sub />
      <Note color="blue">
        Solde débiteur de 7508 en fin d'année = charges réelles supérieures aux acomptes → supplément à facturer aux locataires.<br />
        Solde créditeur = acomptes supérieurs aux charges réelles → remboursement à effectuer aux locataires.
      </Note>

      <H2>6. Points clés</H2>
      {[
        'Immeuble au bilan = max coût d\'acquisition (jamais réévalué à la hausse) — art. 960a CO.',
        'Travaux d\'entretien = charge (7510) | Travaux d\'amélioration = capitalisation (1600).',
        'Immeuble mixte : loyer fictif à soi-même obligatoire — principe de non-compensation art. 958c CO.',
        'Intérêts hypothécaires → charge hors exploitation (7510), pas en 6900.',
        '7508 Décompte charges = compte de transit entre charges chauffage et acomptes locataires.',
        'Amortissements immeuble → 7510 (pas en 6800 qui est pour l\'exploitation).',
      ].map((pt, i) => (
        <div key={i} style={{ display: 'flex', gap: 10, padding: '5px 0', fontSize: '0.85rem', color: '#475569' }}>
          <span style={{ color: '#2563eb', fontWeight: 700, flexShrink: 0 }}>→</span><span>{pt}</span>
        </div>
      ))}
    </div>
  );
}

// ─── ONGLET 3 : RÉSERVES LATENTES ────────────────────────────────────────────
function TabReservesLatentes() {
  return (
    <div>
      <H2>1. Définition</H2>
      <P>Les réserves latentes sont des <strong>richesses cachées</strong> qui existent dans l'entreprise sans apparaître dans le bilan officiel. Elles représentent la différence entre la valeur réelle (économique) du patrimoine et la valeur comptable présentée au bilan.</P>
      <Note color="blue">
        Cette particularité est <strong>propre au droit suisse</strong>. Les normes internationales (IFRS, US GAAP) imposent au contraire une présentation à la "juste valeur" et interdisent la dissimulation de richesses.
      </Note>

      <H2>2. Les deux types de réserves latentes</H2>

      <H3>Réserves latentes involontaires</H3>
      <P>Elles résultent de l'application obligatoire du principe de prudence. Lorsqu'un actif prend de la valeur sur le marché, le droit suisse interdit de le réévaluer à la hausse (art. 960a CO). La différence entre la valeur de marché et la valeur comptable constitue une réserve latente que l'entreprise n'a pas choisie.</P>
      <Note color="yellow">
        Exemple : un immeuble acheté CHF 500'000 vaut aujourd'hui CHF 750'000 sur le marché. Au bilan, il figure pour CHF 500'000 moins les amortissements (admettons CHF 100'000 cumulés = CHF 400'000 net). La réserve latente est de CHF 350'000 (750'000 − 400'000).
      </Note>

      <H3>Réserves latentes volontaires</H3>
      <P>Elles résultent de décisions délibérées utilisant les libertés offertes par le CO :</P>
      <Tableau
        headers={['Mécanisme', 'Base légale', 'Exemple concret']}
        rows={[
          { cells: ['Amortissements excédentaires', 'Art. 960a al. 4 CO', 'Amortir un véhicule à 40% au lieu de 25% économiquement justifié'] },
          { cells: ['Provisions excessives', 'Art. 960e al. 4 CO', 'Provisionner CHF 50\'000 pour un litige estimé à CHF 20\'000'] },
          { cells: ['Provisions maintenues', 'Art. 960e al. 4 CO', 'Garder une provision après que le risque a disparu'] },
          { cells: ['Non-réévaluation des titres', 'Art. 960b CO', 'Ne pas réévaluer des actions qui ont gagné CHF 5\'000 de valeur'] },
          { cells: ['Stock à 2/3 de la valeur', 'Pratique fiscale suisse', 'Valoriser un stock de CHF 30\'000 à CHF 20\'000 (réserve CHF 10\'000)'] },
        ]}
      />

      <H2>3. Constitution des réserves latentes</H2>
      <P>La constitution d'une réserve latente <strong>péjore le résultat déclaré</strong> — elle réduit le bénéfice affiché, donc l'impôt dû. Elle se fait en augmentant les charges ou en réduisant les actifs au-delà de ce qui est économiquement nécessaire.</P>
      <Ecriture debit="6800 Amortissements" credit="1539 FAC Véhicules" montant="8'000" libelle="Amortissement 40% sur véhicule CHF 20'000 — taux maximal fiscal (réserve latente constituée)" />
      <Ecriture debit="6350 Pertes sur débiteurs" credit="1109 Ducroire" montant="3'000" libelle="Provision ducroire 5% — constitution d'une réserve latente" sub />
      <Ecriture debit="4800 Variation de stock" credit="1200 Stock" montant="xxx" libelle="Stock valorisé à 2/3 — 1/3 mis en réserve latente" sub />

      <H2>4. Dissolution des réserves latentes</H2>
      <P>La dissolution d'une réserve latente <strong>améliore le résultat</strong> — elle augmente le bénéfice affiché. Elle peut être volontaire (en cas de besoin de liquidités ou de résultat) ou involontaire (lors de la vente d'un actif).</P>
      <Tableau
        caption="Mécanismes de dissolution"
        headers={['Mécanisme', 'Écriture', 'Effet']}
        rows={[
          { cells: ['Dissolution de provision excessive', 'Débit 2330 Provision / Crédit 7800 Dissolution provision', 'Produit exceptionnel — améliore le résultat'] },
          { cells: ['Vente d\'un actif sous-évalué', 'Produit de cession > valeur comptable → gain sur vente', 'Résultat exceptionnel positif automatique'] },
          { cells: ['Non-renouvellement d\'amortissement', 'Pas d\'écriture — juste moins de charge cette année', 'Résultat courant amélioré'] },
          { cells: ['Réévaluation volontaire du stock', 'Débit 1200 Stock / Crédit 3901 Variation de stock', 'Améliore le résultat de l\'exercice'] },
        ]}
      />
      <Loi art="Art. 959c al. 1 ch. 3 CO — Obligation de transparence">
        L'annexe aux comptes indique le montant global des réserves latentes dissoutes lors du calcul du résultat si ces dissolutions ont amélioré sensiblement le résultat présenté.
      </Loi>
      <Note color="red">Attention : si la dissolution de réserves latentes permet d'afficher un bénéfice alors qu'il y aurait eu une perte sans cette dissolution, l'information doit obligatoirement figurer dans l'annexe aux comptes.</Note>

      <H2>5. Bilan interne vs bilan externe</H2>
      <P>Les entreprises qui gèrent activement leurs réserves latentes tiennent parfois deux visions de leur bilan :</P>
      <Tableau
        headers={['', 'Bilan externe (CO)', 'Bilan interne']}
        rows={[
          { cells: ['Destinataires', 'Fisc, banques, tiers, administration', 'Direction, actionnaires, conseil d\'administration'] },
          { cells: ['Méthode', 'Valeurs prudentes selon CO', 'Valeurs économiques réelles (marché)'] },
          { cells: ['Capitaux propres', 'Sous-évalués (réserves cachées)', 'Valeur réelle du patrimoine'] },
          { cells: ['Réserves latentes', 'Non visibles dans les chiffres', 'Quantifiées et analysées'] },
          { cells: ['Exemple immeuble', 'CHF 400\'000 (coût − amort.)', 'CHF 750\'000 (valeur de marché)'] },
        ]}
      />
      <Note color="blue">
        La différence entre les fonds propres du bilan interne et ceux du bilan externe = <strong>valeur totale des réserves latentes</strong>. C'est un indicateur clé de la solidité financière réelle de l'entreprise.
      </Note>

      <H2>6. Pourquoi constituer des réserves latentes ?</H2>
      <Tableau
        headers={['Motif', 'Explication pratique']}
        rows={[
          { cells: ['Optimisation fiscale', 'Résultat déclaré plus faible → moins d\'impôts sur le bénéfice'] },
          { cells: ['Solidité financière', 'Réserves dissoutes en années difficiles pour éviter une perte officielle'] },
          { cells: ['Lissage du résultat', 'Constituer en années prospères, dissoudre en années creuses'] },
          { cells: ['Rétention des liquidités', 'Bénéfice réduit → moins de pression pour distribuer des dividendes'] },
          { cells: ['Protection contre les créanciers', 'Patrimoine apparent plus faible → moins attractif pour les poursuites'] },
        ]}
      />
      <Note color="green">Les réserves latentes sont <strong>légales en droit suisse</strong> et font partie de la culture fiduciaire helvétique. Elles constituent un outil de gestion fiscale et financière à maîtriser parfaitement.</Note>

      <H2>7. Points clés à retenir</H2>
      {[
        'Réserves latentes = richesses cachées entre valeur réelle et valeur comptable.',
        'Involontaires = interdiction légale de réévaluer les actifs (art. 960a CO).',
        'Volontaires = amortissements excessifs, provisions excessives, stock à 2/3 (art. 960a al. 4 et 960e al. 4 CO).',
        'Constitution → résultat déclaré plus faible → moins d\'impôts.',
        'Dissolution → résultat amélioré → à mentionner dans l\'annexe si sensible (art. 959c CO).',
        'Bilan interne = valeurs réelles | Bilan externe = valeurs prudentes CO.',
        'Pratique légale et courante en Suisse — inexistante sous IFRS.',
      ].map((pt, i) => (
        <div key={i} style={{ display: 'flex', gap: 10, padding: '5px 0', fontSize: '0.85rem', color: '#475569' }}>
          <span style={{ color: '#2563eb', fontWeight: 700, flexShrink: 0 }}>→</span><span>{pt}</span>
        </div>
      ))}
    </div>
  );
}

// ─── ONGLET 4 : COMPTE PRIVÉ & INDÉPENDANT ──────────────────────────────────
function TabPrive() {
  return (
    <div>
      <Section title="1. L'indépendant et son compte privé" defaultOpen={true}>
      <P>
        Dans une entreprise individuelle (raison individuelle), le propriétaire et l'entreprise ne forment qu'une seule entité juridique. Contrairement à la SA où actionnaires et société sont séparés, l'indépendant peut librement prélever ou apporter des fonds. Le <strong>compte privé (2800)</strong> est le journal de bord de tous ces mouvements.
      </P>

      {/* Visual: Schéma sphère privée vs commerciale */}
      <div style={{ margin: '20px 0', display: 'flex', gap: 20, flexWrap: 'wrap', justifyContent: 'center' }}>
        <div style={{ flex: '1 1 200px', maxWidth: 280, padding: 20, background: '#eff6ff', borderRadius: 14, border: '2px solid #2563eb', textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}><Factory size={24} color="#2563eb" /></div>
          <div style={{ fontWeight: 700, color: '#2563eb', fontSize: '0.9rem', marginBottom: 6 }}>Sphère commerciale</div>
          <div style={{ fontSize: '0.78rem', color: '#475569', lineHeight: 1.6 }}>
            Atelier, machines, stock<br />Compte bancaire pro<br />Créances clients<br />Dettes fournisseurs
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column', justifyContent: 'center', padding: '0 10px' }}>
          <div style={{ fontWeight: 700, color: '#2563eb', fontSize: '0.85rem', marginBottom: 4 }}>Compte 2800</div>
          <div style={{ fontSize: '1.5rem' }}>⇄</div>
          <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 4 }}>Prélèvements ←<br />→ Apports</div>
        </div>
        <div style={{ flex: '1 1 200px', maxWidth: 280, padding: 20, background: '#fef3e8', borderRadius: 14, border: '2px solid #ea580c', textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}><Home size={24} color="#ea580c" /></div>
          <div style={{ fontWeight: 700, color: '#ea580c', fontSize: '0.9rem', marginBottom: 6 }}>Sphère privée</div>
          <div style={{ fontSize: '0.78rem', color: '#475569', lineHeight: 1.6 }}>
            Logement, voiture privée<br />Assurance maladie<br />Dépenses personnelles<br />Épargne personnelle
          </div>
        </div>
      </div>

      <P>
        Max Kessler, maître horloger indépendant au Locle, est un ami de longue date de Théo Keller. Il tient un petit atelier où il réalise des travaux de finition haut de gamme pour SwiSSwatch SA. Voici comment il utilise son compte privé au quotidien.
      </P>
      </Section>

      <Section title="2. Les mouvements du compte privé — Exemple annuel de Max" defaultOpen={false}>

      <P>
        Voici les mouvements du compte privé de Max sur l'année 2024. Le <strong>débit</strong> du compte 2800 signifie que Max prélève de la valeur dans l'entreprise. Le <strong>crédit</strong> signifie qu'il apporte de la valeur.
      </P>

      <Tableau
        titre="Mouvements du compte privé 2800 — Max Kessler 2024"
        headers={['Date', 'Opération', 'Débit', 'Crédit', 'Contrepartie']}
        rows={[
          { cells: ['15.01', 'Prélèvement espèces', "3'000", '', '1000 Caisse'], _mono: true },
          { cells: ['01.02', 'Assurance maladie privée', '480', '', '1020 Banque'], _mono: true },
          { cells: ['15.03', 'Apport personnel', '', "10'000", '1020 Banque'], _mono: true },
          { cells: ['01.04', 'Prestation propre (réparation montre)', '450', '', '3710 Consommations propres'], _mono: true },
          { cells: ['15.05', 'Prélèvement espèces', "2'500", '', '1000 Caisse'], _mono: true },
          { cells: ['01.07', 'Part privée véhicule (semestriel)', "2'100", '', '6200 Charges véhicules'], _mono: true },
          { cells: ['15.08', 'Prélèvement virement', "4'000", '', '1020 Banque'], _mono: true },
          { cells: ['01.10', 'Prestation propre (cadeau ami)', "1'200", '', '3710 Consommations propres'], _mono: true },
          { cells: ['15.11', 'Prélèvement espèces', "2'000", '', '1000 Caisse'], _mono: true },
          { cells: ['01.12', 'Part privée véhicule (semestriel)', "2'100", '', '6200 Charges véhicules'], _mono: true },
          { cells: ['31.12', 'Loyer fictif appartement (12 mois)', "18'000", '', '7500 Produits immeubles'], _mono: true },
          { cells: ['', '', '', '', ''], _sep: true },
          { cells: ['', 'Total débit (prélèvements)', "35'830", '', ''], _bold: true, _mono: true },
          { cells: ['', 'Total crédit (apports)', '', "10'000", ''], _bold: true, _mono: true },
          { cells: ['', 'Solde débiteur (capital diminue)', "25'830", '', ''], _bold: true, _mono: true },
        ]}
      />

      <Note color="yellow">
        <strong>Solde débiteur de CHF 25'830 :</strong> Max a prélevé CHF 25'830 de plus qu'il n'a apporté. Son capital propre diminuera d'autant au bouclement. S'il avait gagné CHF 60'000 de bénéfice, son capital net augmenterait quand même de CHF 60'000 − CHF 25'830 = CHF 34'170.
      </Note>
      </Section>

      <Section title="3. Les écritures détaillées" defaultOpen={false}>

      <H3>Prélèvement en espèces</H3>
      <P>Max retire CHF 3'000 de la caisse pour ses dépenses personnelles.</P>
      <Ecriture debit="2800 Privé" credit="1000 Caisse" montant="3'000" libelle="Prélèvement en espèces — besoins personnels" />

      <H3>Assurance maladie privée</H3>
      <P>L'entreprise paie la prime d'assurance maladie de Max (CHF 480/mois). Ce n'est pas une charge d'entreprise — c'est un prélèvement privé.</P>
      <Ecriture debit="2800 Privé" credit="1020 Banque" montant="480" libelle="Assurance maladie privée — prime mensuelle" />

      <H3>Apport personnel</H3>
      <P>Max transfère CHF 10'000 de son épargne personnelle vers le compte bancaire de l'atelier pour renforcer la trésorerie.</P>
      <Ecriture debit="1020 Banque" credit="2800 Privé" montant="10'000" libelle="Apport fonds personnels — renforcement trésorerie" />

      <H3>Part privée du véhicule</H3>
      <P>Max utilise son véhicule utilitaire à 35% pour des trajets privés. Valeur : CHF 38'000. Part privée annuelle forfaitaire : CHF 38'000 × 0.9% × 12 × 35% ≈ CHF 4'200 (comptabilisé en 2 fois).</P>
      <Ecriture debit="2800 Privé" credit="6200 Charges véhicules" montant="2'100" libelle="Part privée véhicule — 1er semestre" />
      </Section>

      <Section title="4. Les prestations propres (compte 3710)" defaultOpen={false}>
      <P>
        Lorsque Max utilise les compétences ou le matériel de son atelier pour un usage privé, c'est une <strong>prestation propre</strong>. Elle doit être évaluée à la <strong>valeur marchande</strong> (ce qu'un client paierait) et comptabilisée comme un produit.
      </P>

      <Tableau
        titre="Prestations propres de Max — 2024"
        headers={['Description', 'Pièces', 'Main-d\'œuvre', 'Total', 'TVA 8.1%']}
        rows={[
          { cells: ['Réparation montre personnelle', '120', '330', '450', '36.45'], _mono: true },
          { cells: ['Fabrication cadeau ami (montre gravée)', '480', '720', "1'200", '97.20'], _mono: true },
          { cells: ['', '', '', '', ''], _sep: true },
          { cells: ['Total prestations propres', '', '', "1'650", '133.65'], _bold: true, _mono: true },
        ]}
      />

      <Ecriture debit="2800 Privé" credit="3710 Consommations propres" montant="1'650" libelle="Prestations propres annuelles (2 opérations)" />
      <Ecriture debit="2800 Privé" credit="2200 TVA due" montant="133.65" libelle="TVA sur prestations propres (8.1%)" sub />

      <Note color="red">
        <strong>Attention TVA !</strong> Les prestations propres sont soumises à la TVA (art. 31 LTVA). Max doit déclarer CHF 133.65 de TVA supplémentaire dans son décompte. C'est une erreur fréquente de l'oublier.
      </Note>
      <Loi art="Art. 31 LTVA">
        Sont réputées prestations à soi-même les prestations qu'un assujetti effectue pour ses besoins propres ou pour ceux de son personnel, dans la mesure où elles auraient donné droit à la déduction de l'impôt préalable en cas d'acquisition auprès d'un tiers.
      </Loi>
      </Section>

      <Section title="5. Le bouclement du compte privé" defaultOpen={false}>
      <P>
        En fin d'année, le solde du compte privé est viré au compte capital. Le capital de Max évolue ainsi :
      </P>

      <Tableau
        titre="Évolution du capital de Max Kessler — 2024"
        headers={['Élément', 'Montant']}
        rows={[
          { cells: ['Capital au 01.01.2024', "45'000"], _mono: true },
          { cells: ['+ Bénéfice de l\'exercice 2024', "+58'000"], _mono: true },
          { cells: ['− Solde débiteur du compte privé', "−25'830"], _mono: true },
          { cells: ['', ''], _sep: true },
          { cells: ['= Capital au 31.12.2024', "77'170"], _bold: true, _mono: true },
        ]}
      />

      <P>L'écriture de clôture :</P>
      <Ecriture debit="2800 Capital" credit="2800 Privé" montant="25'830" libelle="Virement solde privé au capital — clôture 31.12.2024" />

      <Note color="blue">
        <strong>SA vs Indépendant :</strong> Dans une SA comme SwiSSwatch SA, il n'y a PAS de compte privé. Théo Keller ne peut pas se servir dans la caisse. Il perçoit un <strong>salaire</strong> (charge pour la SA) et peut recevoir un <strong>dividende</strong> (soumis à l'impôt anticipé de 35%). C'est la séparation juridique entre la personne et la société.
      </Note>
      </Section>

      <Section title="6. Points clés" defaultOpen={false}>
      {[
        'Compte privé 2800 = interface entre sphère privée et sphère commerciale de l\'indépendant.',
        'Débit 2800 = le propriétaire prend (prélèvement) | Crédit 2800 = il apporte.',
        'Prestations propres (3710) = utilisation privée des produits/services — à la valeur marchande + TVA.',
        'Les prestations propres sont soumises à la TVA (art. 31 LTVA) — erreur fréquente !',
        'En fin d\'année, le solde privé est viré au capital : Capital + Bénéfice − Privé = Nouveau capital.',
        'Pas de compte privé dans la SA — seuls salaire et dividende sont possibles.',
      ].map((pt, i) => (
        <div key={i} style={{ display: 'flex', gap: 10, padding: '5px 0', fontSize: '0.85rem', color: '#475569' }}>
          <span style={{ color: '#2563eb', fontWeight: 700, flexShrink: 0 }}>→</span><span>{pt}</span>
        </div>
      ))}
      </Section>
    </div>
  );
}

// ─── ONGLET 5 : IMMEUBLES EN DÉTAIL ─────────────────────────────────────────
function TabImmeublesDetail() {
  return (
    <div>
      <Section title="1. Immeuble d'exploitation vs immeuble de rendement" defaultOpen={true}>
      <P>
        La distinction entre immeuble d'exploitation et immeuble de rendement est fondamentale en comptabilité suisse. Elle détermine où apparaissent les charges et produits dans le compte de résultat.
      </P>

      {/* Visual: 2 cards comparing the two types */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', margin: '20px 0' }}>
        <div style={{ flex: '1 1 280px', padding: 20, background: '#eff6ff', borderRadius: 14, border: '2px solid #2563eb' }}>
          <div style={{ fontWeight: 700, color: '#2563eb', fontSize: '0.95rem', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}><Factory size={18} /> Immeuble d'exploitation</div>
          <div style={{ fontSize: '0.82rem', color: '#475569', lineHeight: 1.8 }}>
            <strong>Usage :</strong> Atelier, bureau, entrepôt de l'entreprise<br />
            <strong>Compte :</strong> 1600 Immeubles d'exploitation<br />
            <strong>Amortissement :</strong> 6800 (2e niveau — exploitation)<br />
            <strong>Produits :</strong> Aucun (usage interne)<br />
            <strong>Position CR :</strong> Résultat d'exploitation
          </div>
        </div>
        <div style={{ flex: '1 1 280px', padding: 20, background: '#ecfdf5', borderRadius: 14, border: '2px solid #059669' }}>
          <div style={{ fontWeight: 700, color: '#059669', fontSize: '0.95rem', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}><Building2 size={18} /> Immeuble de rendement</div>
          <div style={{ fontSize: '0.82rem', color: '#475569', lineHeight: 1.8 }}>
            <strong>Usage :</strong> Loué à des tiers (locataires)<br />
            <strong>Compte :</strong> 1600 Immeubles (ou sous-compte dédié)<br />
            <strong>Amortissement :</strong> 7510 Charges immeubles (3e niveau)<br />
            <strong>Produits :</strong> 7500 Produits des immeubles — loyers<br />
            <strong>Position CR :</strong> Résultat hors exploitation
          </div>
        </div>
      </div>
      </Section>

      <Section title="2. Acquisition d'un immeuble — L'atelier de Max" defaultOpen={false}>
      <P>
        Max Kessler achète un immeuble au Locle pour CHF 500'000. Le rez-de-chaussée servira d'atelier (60%) et l'étage sera son appartement privé (40%). Voici le financement :
      </P>

      {/* Visual: financing breakdown */}
      <div style={{ margin: '16px 0', padding: 20, background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0' }}>
        <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#0f172a', marginBottom: 12 }}>Financement de l'immeuble — CHF 500'000</div>
        <div style={{ display: 'flex', gap: 0, borderRadius: 8, overflow: 'hidden', height: 36, marginBottom: 12 }}>
          <div style={{ width: '30%', background: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.72rem', fontWeight: 700 }}>Fonds propres 30%</div>
          <div style={{ width: '50%', background: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.72rem', fontWeight: 700 }}>1ère hypothèque 50%</div>
          <div style={{ width: '20%', background: '#ea580c', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.72rem', fontWeight: 700 }}>2e hyp. 20%</div>
        </div>
        <div style={{ display: 'flex', gap: 16, fontSize: '0.78rem', color: '#475569' }}>
          <span><strong style={{ color: '#2563eb' }}>CHF 150'000</strong> apport personnel</span>
          <span><strong style={{ color: '#059669' }}>CHF 250'000</strong> à 1.8%</span>
          <span><strong style={{ color: '#ea580c' }}>CHF 100'000</strong> à 2.5%</span>
        </div>
      </div>

      <Ecriture debit="1600 Immeubles d'exploitation" credit="1020 Banque" montant="150'000" libelle="Acompte achat immeuble (fonds propres 30%)" />
      <Ecriture debit="1600 Immeubles d'exploitation" credit="2450 Dettes hypothécaires" montant="250'000" libelle="1ère hypothèque (50%)" sub />
      <Ecriture debit="1600 Immeubles d'exploitation" credit="2450 Dettes hypothécaires" montant="100'000" libelle="2e hypothèque (20%)" sub />

      <Note color="blue">
        <strong>Règle bancaire :</strong> La 1ère hypothèque couvre généralement jusqu'à 65% de la valeur (taux plus bas). La 2e hypothèque couvre de 65% à 80% (taux plus élevé et amortissement obligatoire). Au-delà de 80%, les banques ne prêtent généralement pas.
      </Note>
      </Section>

      <Section title="3. Comptabilisation courante — Budget annuel de l'immeuble" defaultOpen={false}>

      <Tableau
        titre="Budget annuel immeuble de Max Kessler — 2024"
        headers={['Poste', 'Montant annuel', 'Compte', 'Fréquence']}
        rows={[
          { cells: ['Intérêts 1ère hypothèque (1.8%)', "4'500", '6900 Charges financières', 'Trimestriel'], _mono: true },
          { cells: ['Intérêts 2e hypothèque (2.5%)', "2'500", '6900 Charges financières', 'Trimestriel'], _mono: true },
          { cells: ['Amortissement 2e hypothèque', "5'000", '2450 Dettes hypothécaires', 'Annuel'], _mono: true },
          { cells: ['Amortissement immeuble (1%)', "5'000", '6800 / 1609', 'Annuel'], _mono: true },
          { cells: ['Assurance bâtiment', "1'800", '6300 Assurances', 'Annuel'], _mono: true },
          { cells: ['Entretien courant', "3'000", '7510 Charges immeubles', 'Variable'], _mono: true },
          { cells: ['Chauffage et charges', "4'800", '7510 / 2800 Privé', 'Mensuel'], _mono: true },
          { cells: ['Loyer fictif appartement (40%)', "18'000", '2800 Privé → 7500', 'Mensuel'], _mono: true },
          { cells: ['', '', '', ''], _sep: true },
          { cells: ['Total charges', "44'600", '', ''], _bold: true, _mono: true },
          { cells: ['Total produits (loyer fictif)', "18'000", '', ''], _bold: true, _mono: true },
          { cells: ['Charge nette', "26'600", '', ''], _bold: true, _mono: true },
        ]}
      />

      <H3>Intérêts hypothécaires (trimestriels)</H3>
      <Ecriture debit="6900 Charges financières" credit="1020 Banque" montant="1'750" libelle="Intérêts hypothécaires T1 (4500+2500)/4" />

      <H3>Amortissement de la 2e hypothèque</H3>
      <P>La 2e hypothèque doit être amortie (remboursée) régulièrement, contrairement à la 1ère qui peut rester constante.</P>
      <Ecriture debit="2450 Dettes hypothécaires" credit="1020 Banque" montant="5'000" libelle="Amortissement 2e hypothèque — annuel" />

      <H3>Entretien vs Plus-value</H3>

      <Tableau
        titre="Distinction entretien / plus-value"
        headers={['Type', 'Exemple', 'Traitement', 'Compte']}
        rows={[
          { cells: ['Entretien (maintien)', 'Peinture, réparation toiture, remplacement chaudière à l\'identique', 'Charge immédiate', '7510 Charges immeubles'] },
          { cells: ['Plus-value (amélioration)', 'Agrandissement, nouvelle cuisine, isolation énergétique', 'Capitalisation à l\'actif', '1600 Immeubles'] },
        ]}
      />

      <Note color="yellow">
        <strong>Piège fiscal :</strong> L'administration fiscale surveille de près cette distinction. Capitaliser un entretien courant gonfle artificiellement l'actif et réduit les charges (donc augmente l'impôt futur). À l'inverse, passer une plus-value en charge réduit le bénéfice imposable immédiatement.
      </Note>
      </Section>

      <Section title="4. Immeuble à usage mixte — Le cas de Max" defaultOpen={false}>
      <P>
        L'immeuble de Max est utilisé à 60% pour l'atelier et 40% pour son appartement. Cela crée deux obligations comptables :
      </P>

      {/* Visual: Building split */}
      <div style={{ margin: '16px 0', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ width: 200, borderRadius: '12px 12px 0 0', overflow: 'hidden', border: '2px solid #e2e8f0' }}>
          <div style={{ background: '#fef3e8', padding: 16, textAlign: 'center', borderBottom: '2px dashed #e2e8f0' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#ea580c' }}>ÉTAGE — 40% PRIVÉ</div>
            <div style={{ fontSize: '0.68rem', color: '#94a3b8', marginTop: 4 }}>Appartement de Max</div>
            <div style={{ fontSize: '0.72rem', color: '#ea580c', fontWeight: 600, marginTop: 4 }}>Loyer fictif : CHF 1'500/mois</div>
          </div>
          <div style={{ background: '#eff6ff', padding: 16, textAlign: 'center' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#2563eb' }}>REZ — 60% PROFESSIONNEL</div>
            <div style={{ fontSize: '0.68rem', color: '#94a3b8', marginTop: 4 }}>Atelier d'horlogerie</div>
            <div style={{ fontSize: '0.72rem', color: '#2563eb', fontWeight: 600, marginTop: 4 }}>Charges d'exploitation</div>
          </div>
        </div>
      </div>

      <P>
        <strong>1. Loyer fictif :</strong> Max doit comptabiliser un loyer fictif de CHF 1'500/mois pour son appartement. Ce loyer correspond à ce qu'il devrait payer s'il louait un appartement équivalent sur le marché.
      </P>
      <Ecriture debit="2800 Privé" credit="7500 Produits immeubles" montant="1'500" libelle="Loyer fictif appartement privé — mensuel" />

      <P>
        <strong>2. Répartition des charges communes :</strong> Le chauffage, l'électricité des parties communes, et l'assurance bâtiment doivent être répartis entre la part professionnelle (60%) et la part privée (40%).
      </P>

      <Tableau
        titre="Répartition chauffage annuel CHF 4'800"
        headers={['Part', 'Pourcentage', 'Montant', 'Écriture']}
        rows={[
          { cells: ['Professionnelle (atelier)', '60%', "2'880", 'Débit 7510 / Crédit 1020'], _mono: true },
          { cells: ['Privée (appartement)', '40%', "1'920", 'Débit 2800 / Crédit 1020'], _mono: true },
          { cells: ['Total', '100%', "4'800", ''], _bold: true, _mono: true },
        ]}
      />
      </Section>

      <Section title="5. Vente d'un immeuble — Gain ou perte" defaultOpen={false}>
      <P>
        Après plusieurs années, Max décide de vendre l'immeuble. Voici comment calculer et comptabiliser le résultat de la vente :
      </P>

      <Tableau
        titre="Calcul du gain sur vente — Immeuble de Max"
        headers={['Élément', 'Montant']}
        rows={[
          { cells: ['Prix d\'acquisition (2020)', "500'000"], _mono: true },
          { cells: ['− Amortissements cumulés (4 ans × 1%)', "−20'000"], _mono: true },
          { cells: ['= Valeur comptable nette (VCN)', "480'000"], _bold: true, _mono: true },
          { cells: ['', ''], _sep: true },
          { cells: ['Prix de vente (2024)', "620'000"], _mono: true },
          { cells: ['− VCN', "−480'000"], _mono: true },
          { cells: ['= Gain sur la vente', "140'000"], _bold: true, _mono: true },
        ]}
      />

      <H3>Écritures de vente</H3>
      <Ecriture debit="1020 Banque" credit="1600 Immeubles" montant="480'000" libelle="Sortie immeuble à la valeur comptable nette" />
      <Ecriture debit="1609 Ajust. valeur immeubles" credit="1600 Immeubles" montant="20'000" libelle="Dissolution des amortissements cumulés" sub />
      <Ecriture debit="1020 Banque" credit="8100 Produits hors exploitation" montant="140'000" libelle="Gain sur vente immeuble" sub />

      <Note color="red">
        <strong>Impôt sur les gains immobiliers :</strong> En Suisse, le gain immobilier est soumis à un impôt cantonal spécifique (pas l'impôt sur le bénéfice ordinaire). À Neuchâtel, l'impôt est calculé sur un <strong>barème progressif</strong> (10% à 30% selon le montant du gain), avec une <strong>réduction de 6% par an</strong> dès la 5e année de détention (max. 60% de réduction après 14 ans). En cas de courte détention (moins de 4 ans), une <strong>majoration</strong> s'applique.
      </Note>

      <Tableau
        titre="Impôt gain immobilier — Canton de Neuchâtel"
        headers={['Élément', 'Détail']}
        rows={[
          { cells: ['Barème de base', 'Progressif : 10% (premiers CHF 5\'000) à 30% (au-delà de CHF 135\'000)'] },
          { cells: ['Détention < 4 ans', 'Majoration de l\'impôt de base (supplément spéculatif)'] },
          { cells: ['Dès la 5e année', 'Réduction de 6% par année supplémentaire'] },
          { cells: ['14 ans et plus', 'Réduction maximale de 60% sur l\'impôt de base'] },
          { cells: ['Donation / succession', 'Pas d\'impôt au transfert — durée de détention initiale conservée'] },
          { cells: ['Remploi', 'Report possible si réinvestissement en Suisse dans un délai fixé'] },
        ]}
      />

      <Note color="blue">
        <strong>Exemple :</strong> Max vend après 10 ans de détention avec un gain de CHF 140'000. Impôt de base selon barème progressif ≈ CHF 29'000. Réduction : 6 ans × 6% = 36%. Impôt final ≈ CHF 29'000 × (1 − 0.36) ≈ CHF 18'560. Les taux varient selon les cantons — consultez toujours l'administration fiscale locale.
      </Note>
      </Section>

      <Section title="6. Points clés" defaultOpen={false}>
      {[
        'Immeuble d\'exploitation (usage propre) → charges au 2e niveau (exploitation).',
        'Immeuble de rendement (loué) → produits et charges au 3e niveau (hors exploitation).',
        'Financement : 1ère hypothèque (≤65%, taux bas) + 2e hypothèque (65-80%, amortissement obligatoire) + fonds propres.',
        'Entretien = charge immédiate (7510) | Plus-value = capitalisation (1600).',
        'Usage mixte : loyer fictif obligatoire (privé → produit) + répartition des charges communes.',
        'Vente : gain = prix de vente − VCN → impôt cantonal sur les gains immobiliers (taux dégressif selon la durée).',
      ].map((pt, i) => (
        <div key={i} style={{ display: 'flex', gap: 10, padding: '5px 0', fontSize: '0.85rem', color: '#475569' }}>
          <span style={{ color: '#2563eb', fontWeight: 700, flexShrink: 0 }}>→</span><span>{pt}</span>
        </div>
      ))}
      </Section>
    </div>
  );
}

// ─── VUE PRINCIPALE ───────────────────────────────────────────────────────────
const TABS = [
  { id: 'titres',           label: 'Titres de placement' },
  { id: 'immeubles-detail', label: 'Les immeubles' },
  { id: 'immeubles',        label: 'Immeubles de rendement' },
  { id: 'prive',            label: 'Compte privé & indépendant' },
  { id: 'reserves',         label: 'Réserves latentes' },
];

export default function HorsExploitationView() {
  const [tab, setTab] = useState('titres');
  return (
    <TheoryLayout title="7. Hors exploitation" tag="tag-base" tagLabel="Hors exploitation" meta="Art. 959b · 960a · 960b CO" tabs={TABS} activeTab={tab} onTabChange={setTab}>
      {tab === 'titres'           && <TabTitres />}
      {tab === 'immeubles'        && <TabImmeubles />}
      {tab === 'reserves'         && <TabReservesLatentes />}
      {tab === 'prive'            && <TabPrive />}
      {tab === 'immeubles-detail' && <TabImmeublesDetail />}
    </TheoryLayout>
  );
}